// HelpLauncher.jsx
//
// The control that opens the operator's manual (#172), in its own window, so it
// can be dragged to a second monitor and read alongside the dashboard. A modal
// or a drawer cannot do that, which is the whole point of the feature.
//
// It is rendered twice with different placement — directly under the hamburger
// in the 56 px collapsed rail on desktop, and in the top-right corner of the
// opened panel on mobile — but it is one control, so it lives here rather than
// being written out twice in App.js. Placement is the caller's job; everything
// below is not.
//
// ── Three details decide whether this works at all ───────────────────────────
//
// 1. window.open MUST run synchronously inside the click handler. A popup opened
//    during a user gesture is allowed by every major browser; the same call
//    after an `await` is a popup-blocked no-op that fails silently. There is
//    deliberately nothing asynchronous in handleClick.
//
// 2. The window is NAMED ('samst-help'). Clicking again then navigates and
//    focuses the window that is already open instead of spawning a second copy —
//    which is what would otherwise happen every time someone forgets it is on
//    the other screen.
//
// 3. Popup versus tab is a HINT, not a guarantee. Browser settings can force a
//    tab, and a tab can still be dragged out, so nothing here depends on it
//    being a real window. That is also why this renders an <a> and not a bare
//    button: if window.open is refused outright it returns null, and the
//    anchor's own target-named navigation still opens the manual. Middle-click
//    and ctrl-click keep working for free. preventDefault is called only on the
//    path where window.open actually succeeded, so the two never both fire.
//
// The manual is static HTML under public/help/, so this URL is served by the
// same process that serves the dashboard and needs no route, no bundle and no
// nginx change.

import React from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { APP_VERSION } from '../appVersion';

export const HELP_URL = '/help/';
export const HELP_WINDOW_NAME = 'samst-help';
const HELP_WINDOW_FEATURES = 'width=900,height=1000';

// `lang` so the manual opens in the language the dashboard is currently set to.
// `v` so the manual can compare the version it was reviewed against with the
// version actually running and say so if they differ — a reader seeing
// "reviewed against 0.1.0-pilot" beside an app reporting something else knows
// what they are holding.
export function helpUrl(language) {
  const lang = language === 'me' ? 'me' : 'en';
  return `${HELP_URL}?lang=${lang}&v=${encodeURIComponent(APP_VERSION)}`;
}

export default function HelpLauncher({ t = (k) => k, language = 'en', sx }) {
  // The tooltip IS the label here — the control is an icon, and an icon on its
  // own is a mystery glyph that people click once to find out what it is. Same
  // string on aria-label so it is not only a hover affordance: one claim, one
  // string, and the screen reader gets the sentence rather than "button".
  const label = t('helpManual');

  const handleClick = (event) => {
    const win = window.open(helpUrl(language), HELP_WINDOW_NAME, HELP_WINDOW_FEATURES);
    if (!win) return; // blocked — let the anchor's own navigation take over
    event.preventDefault();
    // Already open and behind the dashboard: bring it forward rather than
    // leaving the click looking like it did nothing. Browsers may refuse, hence
    // the guard.
    try { win.focus(); } catch { /* refused by the browser; the window is open regardless */ }
  };

  return (
    <Tooltip title={label} enterTouchDelay={0} describeChild={false}>
      <IconButton
        component="a"
        href={helpUrl(language)}
        target={HELP_WINDOW_NAME}
        rel="noopener"
        aria-label={label}
        onClick={handleClick}
        sx={sx}
      >
        <HelpOutlineIcon />
      </IconButton>
    </Tooltip>
  );
}
